import { useEffect, useState } from "react";
import axios from "axios";
import {
  PartyPopper,
  Briefcase,
  Cake,
  Users,
  ArrowRight,
  Mail,
  ChevronDown,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCCASIONS = [
  {
    icon: PartyPopper,
    title: "Eventos",
    desc: "Para convertir un evento en algo mucho más CROC.",
    id: "eventos",
  },
  {
    icon: Briefcase,
    title: "Empresas",
    desc: "Reuniones, equipos, presentaciones y eventos corporativos.",
    id: "empresas",
  },
  {
    icon: Cake,
    title: "Celebraciones",
    desc: "Cumpleaños, fiestas y momentos que merecen algo diferente.",
    id: "celebraciones",
  },
  {
    icon: Users,
    title: "Grupos",
    desc: "Porque compartir está bien. Compartir CROC, mejor.",
    id: "grupos",
  },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  eventType: "Evento",
  eventDate: "",
  guests: "",
  message: "",
};

const Catering = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "CROC Catering Valencia | Eventos, Empresas y Celebraciones";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "CROC también va a tu evento. Descubre CROC Catering para eventos, empresas, celebraciones y grupos en Valencia."
      );
    }
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        topic: "Catering",
        event_type: form.eventType,
        event_date: form.eventDate || undefined,
        guests: form.guests || undefined,
        message: form.message,
      });
      toast.success("Mensaje enviado", {
        description: "Gracias por contarnos tu plan. Te responderemos muy pronto.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("No se pudo enviar", {
        description: "Inténtalo de nuevo en unos segundos.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-2xl border border-brand-charcoal/15 bg-white px-4 py-3 text-sm md:text-base text-brand-charcoal placeholder:text-brand-charcoal/40 focus:outline-none focus:ring-2 focus:ring-brand-pink/60 focus:border-brand-pink transition";

  return (
    <main data-testid="catering-page">
      <section
        data-testid="catering-hero"
        className="bg-brand-charcoal text-white overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <span
              data-testid="catering-eyebrow"
              className="inline-flex items-center gap-2 bg-brand-pink/15 text-brand-pink text-xs md:text-sm font-semibold rounded-full px-4 py-2 uppercase tracking-wide"
            >
              CROC Catering
            </span>
            <h1 className="text-balance mt-6 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              LLEVAMOS EL CROC <span className="text-brand-pink">A TU EVENTO.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
              Eventos, celebraciones, empresas o simplemente una buena excusa
              para juntarse. Tú pones el plan. Nosotros ponemos el CROC. 🐷💗
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                data-testid="catering-cta-quiero"
                href="#catering-contacto"
                className="inline-flex items-center justify-center gap-2 bg-brand-pink text-white font-semibold uppercase tracking-wide rounded-full px-8 py-3.5 hover:bg-brand-pink/90 hover:scale-105 transition-transform"
              >
                Quiero CROC
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                data-testid="catering-cta-info"
                href="#catering-contacto"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/25 text-white font-semibold uppercase tracking-wide rounded-full px-8 py-3.5 hover:bg-white hover:text-brand-charcoal transition-colors"
              >
                Pedir información
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="relative flex justify-center lg:justify-end">
            <div className="relative w-60 md:w-80 lg:w-96 aspect-square">
              <div className="absolute inset-0 bg-brand-pink/15 rounded-full" aria-hidden="true" />
              <div
                className="absolute -inset-3 md:-inset-5 rounded-full border-2 border-dashed border-brand-pink/50 animate-croc-spin"
                aria-hidden="true"
              />
              <img
                src="/brand/croc-mascot.png"
                alt="Mascota de CROC LAB"
                data-testid="catering-mascot-image"
                className="relative w-full h-auto rounded-full drop-shadow-xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="catering-ocasiones" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-pink">
              Ocasiones
            </p>
            <h2 className="mt-3 font-display font-bold tracking-tight text-brand-charcoal text-3xl md:text-4xl">
              CROC DONDE TÚ QUIERAS.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OCCASIONS.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.06} className="h-full">
                <article
                  data-testid={`catering-card-${item.id}`}
                  className="h-full bg-white rounded-3xl p-8 border border-brand-charcoal/10 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center">
                    <item.icon className="w-7 h-7" aria-hidden="true" />
                  </div>
                  <h3 className="mt-6 font-display font-semibold text-xl md:text-2xl text-brand-charcoal uppercase">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-brand-charcoal/70 leading-relaxed">
                    {item.desc}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="catering-mensaje" className="py-16 md:py-24 bg-brand-charcoal text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <h2 className="text-balance font-display font-bold tracking-tight text-3xl md:text-4xl">
              NO IMPORTA DÓNDE. SI HACE CROC,{" "}
              <span className="text-brand-pink">ES CROC.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed">
              Llevamos la experiencia CROC más allá del local para convertir
              eventos, celebraciones y encuentros en algo que se recuerda.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="catering-contacto" data-testid="catering-contacto" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-pink">
              Contacto catering
            </p>
            <h2 className="text-balance mt-3 font-display font-bold tracking-tight text-brand-charcoal text-3xl md:text-4xl">
              ¿TIENES UN PLAN? <span className="text-brand-pink">NOSOTROS TENEMOS EL CROC.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-brand-charcoal/75 leading-relaxed max-w-lg">
              Cuéntanos qué estás preparando y hablamos.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-brand-charcoal">Email</p>
                <a
                  data-testid="catering-email-link"
                  href="mailto:croclab.valencia@gmail.com"
                  className="text-sm text-brand-charcoal/60 hover:text-brand-pink transition-colors"
                >
                  croclab.valencia@gmail.com
                </a>
              </div>
            </div>
            <p className="mt-8 text-xs text-brand-charcoal/45 max-w-md">
              CROC Catering está disponible próximamente: cuéntanos tu plan y te
              avisamos con todos los detalles.
            </p>
            <div
              data-testid="catering-alergenos-notice"
              className="mt-6 bg-white rounded-2xl border border-brand-charcoal/10 p-5 max-w-md"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">
                ¿Alergias o intolerancias?
              </p>
              <p className="mt-2 text-xs md:text-sm text-brand-charcoal/60 leading-relaxed">
                Si tú o alguno de tus invitados tiene una alergia o intolerancia
                alimentaria, consúltanos antes de organizar tu pedido o evento.
              </p>
              <a
                data-testid="catering-alergenos-link"
                href="/alergenos"
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-brand-pink hover:underline"
              >
                Información sobre alérgenos
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <form
              data-testid="catering-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl border border-brand-charcoal/10 shadow-sm p-6 md:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="catering-nombre" className="block text-sm font-semibold text-brand-charcoal mb-2">
                    Nombre
                  </label>
                  <input
                    id="catering-nombre"
                    data-testid="catering-input-nombre"
                    type="text"
                    required
                    maxLength={120}
                    placeholder="Tu nombre"
                    value={form.name}
                    onChange={update("name")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="catering-email" className="block text-sm font-semibold text-brand-charcoal mb-2">
                    Email
                  </label>
                  <input
                    id="catering-email"
                    data-testid="catering-input-email"
                    type="email"
                    required
                    maxLength={200}
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={update("email")}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="catering-telefono" className="block text-sm font-semibold text-brand-charcoal mb-2">
                    Teléfono
                  </label>
                  <input
                    id="catering-telefono"
                    data-testid="catering-input-telefono"
                    type="tel"
                    maxLength={40}
                    placeholder="600 000 000"
                    value={form.phone}
                    onChange={update("phone")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="catering-tipo" className="block text-sm font-semibold text-brand-charcoal mb-2">
                    Tipo de evento
                  </label>
                  <div className="relative">
                    <select
                      id="catering-tipo"
                      data-testid="catering-select-tipo"
                      value={form.eventType}
                      onChange={update("eventType")}
                      className={`${inputClass} appearance-none pr-10`}
                    >
                      <option>Evento</option>
                      <option>Empresa</option>
                      <option>Celebración</option>
                      <option>Grupo</option>
                      <option>Otro</option>
                    </select>
                    <ChevronDown
                      className="w-4 h-4 text-brand-charcoal/50 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="catering-fecha" className="block text-sm font-semibold text-brand-charcoal mb-2">
                    Fecha
                  </label>
                  <input
                    id="catering-fecha"
                    data-testid="catering-input-fecha"
                    type="date"
                    value={form.eventDate}
                    onChange={update("eventDate")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="catering-personas" className="block text-sm font-semibold text-brand-charcoal mb-2">
                    Número aproximado de personas
                  </label>
                  <input
                    id="catering-personas"
                    data-testid="catering-input-personas"
                    type="number"
                    min={1}
                    max={100000}
                    placeholder="Ej. 30"
                    value={form.guests}
                    onChange={update("guests")}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="catering-mensaje" className="block text-sm font-semibold text-brand-charcoal mb-2">
                  Mensaje
                </label>
                <textarea
                  id="catering-mensaje"
                  data-testid="catering-input-mensaje"
                  required
                  maxLength={2000}
                  rows={4}
                  placeholder="Cuéntanos qué estás preparando…"
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputClass} resize-none`}
                />
              </div>
              <div className="space-y-3">
                <label htmlFor="catering-privacidad" className="flex items-start gap-3 cursor-pointer">
                  <input
                    id="catering-privacidad"
                    data-testid="catering-checkbox-privacidad"
                    type="checkbox"
                    required
                    className="mt-1 w-4 h-4 shrink-0 accent-[#FF72AE]"
                  />
                  <span className="text-xs md:text-sm text-brand-charcoal/70 leading-relaxed">
                    He leído y acepto la{" "}
                    <a
                      href="/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="catering-privacidad-link"
                      className="text-brand-pink font-semibold hover:underline"
                    >
                      Política de Privacidad
                    </a>
                    .
                  </span>
                </label>
                <p className="text-xs text-brand-charcoal/50 leading-relaxed">
                  Información básica sobre protección de datos — Responsable:
                  CROC LAB, SOCIEDAD LIMITADA (NIF B88810940). Finalidad:
                  atender tu consulta. Base jurídica: tu consentimiento.
                  Destinatarios: no se ceden datos a terceros. Derechos: acceso,
                  rectificación, supresión, oposición, limitación y portabilidad
                  en croclab.valencia@gmail.com.
                </p>
              </div>
              <button
                data-testid="catering-submit-button"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-pink text-white font-semibold uppercase tracking-wide rounded-full px-8 py-3.5 hover:bg-brand-pink/90 hover:scale-[1.02] transition-transform disabled:opacity-60 disabled:hover:scale-100"
              >
                <Send className="w-4 h-4" aria-hidden="true" />
                {sending ? "Enviando…" : "Hablemos"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Catering;
